import { useCallback, useMemo } from 'react';

import Stack from '@mui/material/Stack';

import { EMPTY_STRING } from '@e-socna/empty';
import type { MissionCategoryId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import {
  buildChildrenMap,
  buildPathToNode,
} from './utils/build-category-tree.util';
import { LevelSelect } from './LevelSelect';
import { LeafLevelInput } from './LeafLevelInput';
import type { MissionCategoryValue } from './types/mission-category-value.type';

interface Props {
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly helperText?: string;
  readonly label?: string;
  readonly name: string;
  readonly onBlur?: () => void;
  readonly onChange: (value: MissionCategoryValue) => void;
  readonly required?: boolean;
  readonly value: MissionCategoryValue;
}

// The tree is browsed level by level down to the node of the leaf, typed
// freely: the reference leaves of the node are only suggestions.
export const MissionCategoryAutocomplete = ({
  disabled,
  error,
  helperText,
  label = 'Catégorie',
  name,
  onBlur,
  onChange,
  required,
  value,
}: Readonly<Props>) => {
  const { missionCategories: categories } = useReferencesContext();

  const childrenMap = useMemo(() => buildChildrenMap(categories), [categories]);

  const path = useMemo(
    () => (value.parentId ? buildPathToNode(categories, value.parentId) : []),
    [categories, value.parentId],
  );

  const childrenOf = (parentId: MissionCategoryId | null) =>
    childrenMap.get(parentId) ?? [];
  const isBranch = (category: Readonly<{ _id: MissionCategoryId }>) =>
    !!childrenOf(category._id).length;

  const handleBranchChange = useCallback(
    (branchId: MissionCategoryId) => {
      onChange({ parentId: branchId, leaf: EMPTY_STRING });
    },
    [onChange],
  );

  const leafChangeHandlerFactory = useCallback(
    (parentId: MissionCategoryId | null) => (leaf: string) => {
      onChange({ parentId, leaf });
    },
    [onChange],
  );

  const levelChangeHandlerFactory = useCallback(
    (parentId: MissionCategoryId | null) =>
      (selectedId: MissionCategoryId | null) => {
        onChange({ parentId: selectedId ?? parentId, leaf: EMPTY_STRING });
      },
    [onChange],
  );

  return (
    <Stack spacing={1}>
      {Array.from({ length: path.length + 1 }, (_, level) => {
        const parentId = level === 0 ? null : (path[level - 1] ?? null);
        const options = childrenOf(parentId);
        const selectedId = path[level] ?? null;
        const isLast = level === path.length;
        const leaves = options.filter((option) => !isBranch(option));
        const levelProps = {
          disabled,
          error: isLast ? error : undefined,
          helperText: isLast ? helperText : undefined,
          initiallyOpen: level > 0 && isLast && !value.leaf,
          label:
            level === 0
              ? `${label}${required ? ' *' : EMPTY_STRING}`
              : undefined,
          name: level === 0 ? name : undefined,
          onBlur: isLast ? onBlur : undefined,
        };

        if (leaves.length) {
          return (
            <LeafLevelInput
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...levelProps}
              branches={options.filter(isBranch)}
              id={`${name}-${level.toString()}`}
              key={parentId ?? 'root'}
              leaf={isLast ? value.leaf : EMPTY_STRING}
              leaves={leaves}
              onBranchChange={handleBranchChange}
              onLeafChange={leafChangeHandlerFactory(parentId)}
              selectedBranchId={selectedId}
            />
          );
        }

        return (
          <LevelSelect
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...levelProps}
            key={parentId ?? 'root'}
            onChange={levelChangeHandlerFactory(parentId)}
            options={options}
            value={selectedId}
          />
        );
      })}
    </Stack>
  );
};
